/**
 * Self-hosted crash reporting (no Sentry): uncaught errors, unhandled promise
 * rejections and React error-boundary catches are sent as compact reports to
 * `POST /api/client-errors`, where they are grouped by fingerprint and shown
 * to superadmins in the admin console (Errors page).
 *
 * What a report carries — and nothing else: message, stack (truncated), the
 * route PATH (no query / hash), the build id, and — implicitly, via the
 * Authorization header the server verifies — the signed-in user's id. The
 * server adds the User-Agent and scrubs emails / tokens again. No request
 * bodies, no state, no cookies (`credentials: 'omit'`).
 *
 * Frugal by construction:
 *   - dedupe: the same error (message + top frame) is sent ONCE per page
 *     session; repeats before the batch leaves only bump its `count`;
 *   - rate: at most MAX_PER_MINUTE reports per minute, MAX_PER_SESSION total;
 *   - batch: reports leave together after BATCH_DELAY_MS (≤ 10 per request),
 *     or immediately on `pagehide` with `keepalive`.
 * Disable with VITE_ERROR_REPORTING=off.
 */
import { API_BASE } from '@/shared/api/client';
import { isChunkLoadError } from '@/shared/pwa/chunk-recovery';
import { getAccessToken } from '@/shared/stores/auth.store';

export type ErrorKind = 'error' | 'unhandledrejection' | 'boundary';

export interface ClientErrorReport {
  message: string;
  stack?: string;
  route: string;
  release: string;
  kind: ErrorKind;
  count: number;
}

export const MAX_PER_MINUTE = 10;
export const MAX_PER_SESSION = 50;
export const MAX_BATCH = 10;
export const BATCH_DELAY_MS = 2_000;
const MAX_MESSAGE = 1000;
const MAX_STACK = 4000;

/** Noise nobody can act on: opaque cross-origin errors, extensions, RO loops. */
const IGNORED_MESSAGE = /^(Script error\.?|ResizeObserver loop (limit exceeded|completed with undelivered notifications)\.?)$/i;
const EXTENSION_STACK = /(chrome|moz|safari(-web)?)-extension:\/\//;

export const RELEASE: string = String(
  (import.meta.env.VITE_APP_VERSION as string | undefined) ??
    new URL(import.meta.url).pathname.split('/').pop() ??
    'dev',
).slice(0, 80);

const reported = new WeakSet<object>();
const sentSignatures = new Set<string>();
let queue: ClientErrorReport[] = [];
const queuedBySignature = new Map<string, ClientErrorReport>();
let windowStart = 0;
let windowCount = 0;
let sessionCount = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

function enabled(): boolean {
  return String(import.meta.env.VITE_ERROR_REPORTING ?? '').toLowerCase() !== 'off';
}

function normalize(reason: unknown): { message: string; stack?: string | undefined } {
  if (reason instanceof Error) {
    return { message: `${reason.name}: ${reason.message}`.slice(0, MAX_MESSAGE), stack: reason.stack?.slice(0, MAX_STACK) };
  }
  if (typeof reason === 'string') return { message: reason.slice(0, MAX_MESSAGE) };
  try {
    return { message: `Non-Error thrown: ${JSON.stringify(reason)}`.slice(0, 200) };
  } catch {
    return { message: 'Non-Error thrown' };
  }
}

/** ApiError (shared/api/client) below 500 — the server already knows; not a crash. */
function isExpectedApiError(reason: unknown): boolean {
  if (!(reason instanceof Error) || reason.name !== 'ApiError') return false;
  const status = (reason as Error & { status?: unknown }).status;
  return typeof status === 'number' && status < 500;
}

function signature(message: string, stack?: string | undefined): string {
  const frame = stack?.split('\n').find((l) => /^\s*at\s|@.+:\d+/.test(l))?.trim() ?? '';
  return `${message}\n${frame}`;
}

function admit(now: number): boolean {
  if (sessionCount >= MAX_PER_SESSION) return false;
  if (now - windowStart >= 60_000) {
    windowStart = now;
    windowCount = 0;
  }
  if (windowCount >= MAX_PER_MINUTE) return false;
  windowCount += 1;
  sessionCount += 1;
  return true;
}

/** Queue one error for reporting. Safe to call from anywhere; never throws. */
export function reportError(reason: unknown, kind: ErrorKind = 'error'): void {
  try {
    if (!enabled()) return;
    if (reason && typeof reason === 'object') {
      if (reported.has(reason)) return;
      reported.add(reason);
    }
    if (isChunkLoadError(reason)) return; // self-heals via chunk-recovery
    if (isExpectedApiError(reason)) return; // 4xx / timeouts are user-facing, not bugs
    const { message, stack } = normalize(reason);
    if (!message || IGNORED_MESSAGE.test(message) || (stack && EXTENSION_STACK.test(stack))) return;

    const sig = signature(message, stack);
    const pending = queuedBySignature.get(sig);
    if (pending) {
      pending.count += 1;
      return;
    }
    if (sentSignatures.has(sig)) return;
    if (!admit(Date.now())) return;

    const report: ClientErrorReport = {
      message,
      ...(stack ? { stack } : {}),
      route: window.location.pathname.slice(0, 300),
      release: RELEASE,
      kind,
      count: 1,
    };
    queuedBySignature.set(sig, report);
    queue.push(report);
    if (queue.length >= MAX_BATCH) flushErrorReports();
    else if (!timer) timer = setTimeout(() => flushErrorReports(), BATCH_DELAY_MS);
  } catch {
    // The reporter must never become the error.
  }
}

/** Send everything queued. `keepalive` lets it survive a page unload. */
export function flushErrorReports(keepalive = false): void {
  if (timer) clearTimeout(timer);
  timer = null;
  if (queue.length === 0) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  for (const [sig, r] of queuedBySignature) {
    if (batch.includes(r)) {
      queuedBySignature.delete(sig);
      sentSignatures.add(sig);
    }
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    void fetch(`${API_BASE}/api/client-errors`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reports: batch }),
      credentials: 'omit',
      keepalive,
    }).catch(() => undefined);
  } catch {
    // Offline / blocked — drop silently.
  }
  if (queue.length > 0) timer = setTimeout(() => flushErrorReports(), BATCH_DELAY_MS);
}

/** Install the global hooks once (main.tsx). */
export function setupErrorReporter(): void {
  if (installed || !enabled()) return;
  installed = true;
  window.addEventListener('error', (event) => {
    // Resource load failures (img/script tags) arrive here without `error`.
    if (!event.error && !event.message) return;
    reportError(event.error ?? event.message, 'error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, 'unhandledrejection');
  });
  window.addEventListener('pagehide', () => flushErrorReports(true));
}

/** Test-only: reset module state between cases. */
export function __resetErrorReporterForTests(): void {
  queue = [];
  queuedBySignature.clear();
  sentSignatures.clear();
  windowStart = 0;
  windowCount = 0;
  sessionCount = 0;
  if (timer) clearTimeout(timer);
  timer = null;
}
